import React, { useState, useCallback } from 'react';
import { Autocomplete, Box } from '@mui/material';
import Button from '@mui/material/Button';
import {
	Theme,
	Typography,
	TextField,
	MenuItem,
	Grid,
	FormControl,
	RadioGroup,
	FormControlLabel,
	Radio,
	Divider,
} from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import classNames from 'classnames';
import Spinner from 'common/components/Spinner';
import { isUniqueInventoryIdentifier } from 'common/api/frontend/utils';
import { useTranslation } from 'services/localization/useTranslation';
import { useDispatch, useSelector } from 'react-redux';
import * as UserSelectors from 'selectors/UserSelectors';
import * as InventorySelectors from 'selectors/InventorySelectors';
import {
	InventoryItem,
	InventoryField,
	SkuItem,
	InventoryFields,
	FixedFieldValues,
	AllocationOption,
} from 'common/modules/inventory/types';
import { uniqBy, uniq } from 'lodash';
import { api } from 'common/frontend/api';
import { newFirestoreId } from 'common/utils/newRentalUtils';
import SpecificationsList from '../components/SpecificationsList';
import { showNotification } from 'actions/NotificationActions';
import { push, Routes } from 'routing';
import Container from 'common/components/Container';
import ContainerBox from 'components/ContainerBox';
import AddSku from '../components/AddSku';
import InventoryIdentifierInput from '../components/InventoryIdentifierInput';
import { getNewInventoryItem, saveNewInventoryFieldsToDb, skuItemsToOptions } from '../utils';
import { FieldValues } from 'common/frontend/firebase/firestore';
import NewSpecificationDialog from '../components/NewSpecificationDialog';
import { getInventoryFieldsArray } from 'common/modules/inventory';
import FixedFieldValuesList from '../components/FixedFieldValuesList';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { getTypedKeys } from 'common/utils/objects';
import { FixedFieldValueOptions } from 'common/modules/inventory/constants';
import { newId } from 'common/utils/common';
import { BatchManager } from 'common/frontend/firebase/firestore';
import {
	allocationOptionValues,
	allocationSelectionToObject,
	getInventoryAllocationOptionTranslation,
} from 'views/Inventory/utils';
import { setOnboardingActionDone } from 'services/onboarding/utils';

type InputEventType = React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>;

interface Props {
	skuItems: SkuItem[];
	inventoryFields: InventoryFields;
	shopId: string;
}

const NEW_SKU_VALUE = '__NEW_SKU__';

const AddInventory = (props: Props) => {
	const classes = useStyles();
	const { skuItems, shopId, inventoryFields: _inventoryFields } = props;
	const userLocations = useSelector(UserSelectors.userLocations);
	const dispatch = useDispatch();
	const [addSkuOpen, setAddSkuOpen] = useState<boolean>(false);
	const [isNewSpecificationDialogOpen, setNewSpecificationDialogOpen] = useState<boolean>(false);
	const { t } = useTranslation();
	const [selectedSkuId, setSelectedSkuId] = useState<string | null>(null);
	const activeLocationId = useSelector(UserSelectors.userActiveLocationId)!;
	const existingInventoryIds = useSelector(InventorySelectors.inventoryItemsIds);
	const [selectedLocationId, setSelectedLocationId] = useState<string>(activeLocationId);
	const useDinVariation = useSelector(ShopSelectors.useDinVariation);
	const shopDinCollectionMethod = useSelector(ShopSelectors.shopDinCollectionMethod);
	const shopAnalytics = useSelector(ShopSelectors.activeShopAnalytics);

	const [autoIds, setAutoIds] = useState<boolean>(true);
	const [amount, setAmount] = useState<number>(1);
	const [allocationValue, setAllocationValue] = useState<AllocationOption>('rental');
	const [isAddingNewItems, setIsAddingNewItems] = useState<boolean>(false);
	const [addingIds, setAddingIds] = useState<string[]>([]);
	const [ids, setIds] = useState<string[]>([]);
	const [tagFieldsToAdd, setTagFieldsToAdd] = useState<{
		[tagId: string]: string | number;
	}>({});
	const [fixedFieldValues, setFixedFieldValues] = useState<FixedFieldValues | undefined>(undefined);
	const [newInventoryFields, setNewInventoryFields] = useState<InventoryField[]>([]);
	const [skuSearchValue, setSkuSearchValue] = useState<string>('');
	const selectedSkuType = skuItems.find((i) => i.id === selectedSkuId)?.itemType ?? null;
	const inventoryFields = uniqBy(
		[...getInventoryFieldsArray(_inventoryFields), ...newInventoryFields],
		'id',
	);
	const visibleFixedValueOptions = getTypedKeys(FixedFieldValueOptions).filter(
		(option) =>
			(option === 'soleLength' && !!shopDinCollectionMethod) ||
			(option === 'dinVariation' && !!useDinVariation),
	);

	const getUniqueIds = useCallback(
		(ids: string[]) => uniq(ids).filter((id: string) => !existingInventoryIds?.includes(id)),
		[existingInventoryIds],
	);

	const skuOptions = skuItemsToOptions(skuItems);
	const selectedSkuOption = !selectedSkuId
		? undefined
		: skuOptions.find((option) => option.id === selectedSkuId);

	const uniqueIds = getUniqueIds(ids);

	const handleAutoIdsChange = () => {
		!autoIds ? setAmount(1) : setAmount(uniqueIds.length);
		setAutoIds(!autoIds);
	};

	const getAutoIds = async (name: string, idAmount: number): Promise<string[]> => {
		const nameStrings = name.toUpperCase().split(' ');
		const charsToTake = nameStrings.length === 1 ? 3 : 2;
		const idPrefix = nameStrings.map((string) => string.substring(0, charsToTake)).join('-');
		let uniqueIds: string[] = [];
		let _numberSuffix = 1;
		let _idsToCreateCount = idAmount;
		try {
			do {
				const numberSuffix = _numberSuffix;
				const idsToCreateCount = _idsToCreateCount;
				const autoIds = new Array(idsToCreateCount)
					.fill(0)
					.map((_, index) =>
						!!idPrefix ? `${idPrefix}-${numberSuffix + index}` : `${numberSuffix + index}`,
					);
				const uniqueIdsArray = await Promise.all(
					autoIds.map((id) => isUniqueInventoryIdentifier(shopId, id.toUpperCase())),
				);
				const newUniqueIds = autoIds.filter((id, index) => !!uniqueIdsArray[index]);
				uniqueIds.push(...newUniqueIds);
				_numberSuffix = numberSuffix + idsToCreateCount;
				_idsToCreateCount = idsToCreateCount - newUniqueIds.length;
			} while (_idsToCreateCount > 0);
			return uniqueIds;
		} catch (e) {
			return new Array(idAmount).fill(0).map((_, index) => newId(8).toUpperCase());
		}
	};

	const addStockItem = async () => {
		const newItems: InventoryItem[] = [];
		if (selectedSkuId === null) {
			return;
		}
		const skuItem = skuItems.find((sku) => sku.id === selectedSkuId);
		if (!skuItem) {
			return;
		}
		setIsAddingNewItems(true);
		if (skuItem.itemType === 'bulk') {
			const existingBulkItems = await api()
				.inventoryItems.get.where('type', '==', 'bulk')
				.where('skuId', '==', skuItem.id)
				.where('locationId', '==', selectedLocationId)
				.get();
			const existingBulkItemId = existingBulkItems[0]?.id;
			const batchManager = new BatchManager();
			if (existingBulkItemId) {
				api(batchManager.batch())
					.inventoryItems.doc(existingBulkItemId)
					.set(
						{
							quantity: FieldValues.increment(amount),
							statuses: {
								IN_USE: FieldValues.increment(amount),
							},
							allocations: {
								[allocationValue]: FieldValues.increment(amount),
							},
						},
						{ merge: true },
					);
			} else {
				const newBulkInventoryItem = getNewInventoryItem({
					id: newFirestoreId(),
					skuCode: skuItem.skuCode,
					skuId: skuItem.id,
					shopId,
					locationId: selectedLocationId,
					identifier: null,
					fieldValues: tagFieldsToAdd,
					skuName: skuItem.skuName,
					type: 'bulk',
					quantity: amount,
					statuses: {
						IN_USE: amount,
					},
					allocations: {
						rental: 0,
						sales: 0,
						[allocationValue]: amount,
					},
					fixedFieldValues,
				});

				api(batchManager.batch())
					.inventoryItems.doc(newBulkInventoryItem.id)
					.set(newBulkInventoryItem);
			}
			if (!!newInventoryFields.length) {
				await saveNewInventoryFieldsToDb(api(batchManager.batch()), newInventoryFields, shopId);
			}
			await batchManager.commit();
		} else {
			const identifiers = autoIds ? await getAutoIds(skuItem.skuName, amount) : uniqueIds;
			setAddingIds(identifiers);
			for (let index = 0; index < amount; index++) {
				const stockItemId = newFirestoreId();
				const identifier = identifiers[index];
				const newInventoryItem = getNewInventoryItem({
					id: stockItemId,
					skuCode: skuItem.skuCode,
					skuId: skuItem.id,
					shopId,
					locationId: selectedLocationId,
					identifier: [identifier ?? newFirestoreId()],
					fieldValues: tagFieldsToAdd,
					skuName: skuItem.skuName,
					type: 'single',
					status: 'IN_USE',
					fixedFieldValues,
					allocation: allocationSelectionToObject(allocationValue),
				});
				newItems.push(newInventoryItem);
			}
			const batchManager = new BatchManager();
			if (!!newInventoryFields.length) {
				await saveNewInventoryFieldsToDb(api(batchManager.batch()), newInventoryFields, shopId);
			}
			newItems.forEach((item) => {
				api(batchManager.batch()).inventoryItems.doc(item.id).set(item);
			});
			await batchManager.commit();
		}
		setIsAddingNewItems(false);
		setOnboardingActionDone('ARTICLE_CREATION', shopAnalytics);
		dispatch(
			showNotification({
				message: t('inventory.addArticles.addedArticles', {
					defaultValue: 'Added {{amount}} articles',
					amount,
				}),
				variant: 'info',
				autoDismissMs: 2000,
			}),
		);
		resetForm();
	};

	const resetForm = () => {
		setAmount(0);
		setIds([]);
		setNewInventoryFields([]);
		setAddingIds([]);
	};

	const handleSelectedSkuId = (id: string | null) => {
		if (id === NEW_SKU_VALUE) {
			setAddSkuOpen(true);
		} else {
			setSelectedSkuId(id);
		}
	};

	const handleLocationChange = (e: InputEventType) => {
		setSelectedLocationId(e.target.value);
	};

	const handleAmountChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
		const value = parseInt(event.target.value, 10);
		const validatedValue = isNaN(value) ? 0 : value;
		// To not allow creating crazy inventory amounts at once
		if (validatedValue > 9999) return;
		setAmount(validatedValue);
	};

	const renderAllocationSelect = () => (
		<TextField
			id="type"
			label={t('inventory.columns.allocation')}
			name="type"
			select
			value={allocationValue}
			onChange={(e) => setAllocationValue(e.target.value as AllocationOption)}
			margin="none"
			type="text"
			variant="outlined"
			sx={{ minWidth: 200 }}
		>
			{allocationOptionValues.map((allocation, idx) => {
				return (
					<MenuItem key={`${allocation}${idx}`} value={allocation}>
						{getInventoryAllocationOptionTranslation(t, allocation)}
					</MenuItem>
				);
			})}
		</TextField>
	);

	const renderAmountSelectorAndAllocation = () => {
		return (
			<Grid container spacing={2} alignItems="center">
				<Grid item>
					<Typography variant="body1">
						{t('inventory.addArticles.productAmount', 'Articles to add')}
					</Typography>
				</Grid>
				<Grid item>
					<TextField
						placeholder={t('inventory.addArticles.amount', 'Amount')}
						type="number"
						variant="outlined"
						onChange={handleAmountChange}
						value={amount === 0 ? '' : amount}
					/>
				</Grid>
				<Grid item>{renderAllocationSelect()}</Grid>
			</Grid>
		);
	};

	const handleIdsChange = (ids: string[]) => {
		setIds(ids);
		setAmount(getUniqueIds(ids).length);
	};

	const renderCustomIdSelector = () => {
		return (
			<>
				<Box mb={2}>{renderAllocationSelect()}</Box>
				<Typography variant={'h6'}>
					{t('inventory.addArticles.addIdentifiers', 'Add identifiers')}
				</Typography>
				<Typography variant={'body2'}>
					{t(
						'inventory.addArticles.addIdentifiersDescription',
						'You can scan the code or copy a list from e.g. Excel',
					)}
				</Typography>
				<Box mt={2} />
				<InventoryIdentifierInput
					shopId={shopId}
					ids={ids}
					onIdsChange={handleIdsChange}
					showClearBtn
					showInvalidIdsHelper
					currentItemIds={addingIds}
					existingInventoryIds={existingInventoryIds}
				/>
				<Typography variant={'body1'} gutterBottom className={classes.marginTop16}>
					{t('inventory.addArticles.productAmount', 'Articles to add')}: {amount}{' '}
					{t('common:pcs', 'pcs')}
				</Typography>
			</>
		);
	};

	const handleAddNewInventoryField = (fieldLabel: string) => {
		const newField: InventoryField = {
			id: newFirestoreId(),
			label: fieldLabel,
			type: 'text',
			values: [],
		};
		setNewInventoryFields((existingField) => [...existingField, newField]);
		setNewSpecificationDialogOpen(false);
	};

	const backToInventory = () => {
		push(Routes.INVENTORY);
	};

	const handleSkuCreated = (skuId: string) => {
		setSelectedSkuId(skuId);
		setOnboardingActionDone('SKU_CREATION', shopAnalytics);
		setAddSkuOpen(false);
	};

	const handleCreateFirstSKU = () => {
		setAddSkuOpen(true);
	};

	const renderAddSkuOnBoard = () => {
		return (
			<ContainerBox>
				<Box
					display="flex"
					justifyContent="center"
					alignItems="center"
					flexDirection="column"
					textAlign="center"
					py={10}
					px={4}
				>
					<Box>
						<Typography variant="h5">
							{t('inventory.addArticles.skuOnBoardTitle', 'Every article needs an SKU')}
						</Typography>
					</Box>
					<Box mt={2}>
						<Typography variant="body1">
							{t(
								'inventory.addArticles.skuOnBoardBody1',
								'To help you organize your articles, Rentle use SKUs - Stock Keeping Units',
							)}
							.
						</Typography>
						<Typography variant="body1">
							{t(
								'inventory.addArticles.skuOnBoardBody2',
								'Use SKUs to define your distinct type of article. Each article belongs to a single SKU.',
							)}
						</Typography>
					</Box>
					<Box mt={6}>
						<Button variant="contained" color="primary" onClick={handleCreateFirstSKU}>
							{t('inventory.addArticles.skuOnBoardConfirmBtn', 'Create a new SKU')}
						</Button>
					</Box>
					<Box mt={2}>
						<Button variant="text" color="secondary" onClick={backToInventory}>
							{t('inventory.addArticles.skuOnBoardCancelBtn', 'Go back')}
						</Button>
					</Box>
				</Box>
			</ContainerBox>
		);
	};

	return (
		<>
			<Container densePaddingTop>
				{skuItems.length === 0 ? (
					renderAddSkuOnBoard()
				) : (
					<ContainerBox>
						<Grid container spacing={2}>
							<Grid item xs={12} sm={6}>
								<Autocomplete
									disablePortal
									value={selectedSkuOption ?? null}
									onChange={(_: any, option) => handleSelectedSkuId(option?.id ?? null)}
									inputValue={skuSearchValue}
									getOptionLabel={(opt) => opt.label}
									onInputChange={(_, newInputValue) => setSkuSearchValue(newInputValue)}
									options={[
										{
											id: NEW_SKU_VALUE,
											label: `+ ${t('inventory.addArticles.skuSelector.new', 'New SKU')}`,
											type: 'single',
										},
										...skuOptions,
									]}
									filterOptions={(options, state) =>
										state.inputValue === ''
											? options
											: options.filter((opt) =>
													opt.id === NEW_SKU_VALUE
														? true
														: opt.label.toLowerCase().includes(state.inputValue.toLowerCase()),
											  )
									}
									renderInput={(params) => (
										<TextField
											{...params}
											className={classNames(classes.textField, classes.ProductToAdd)}
											label={t('inventory.addArticles.skuSelector.empty', 'Choose or Add SKU')}
											variant="outlined"
											placeholder={`${t('common:actions.search')}...`}
										/>
									)}
									renderOption={(props, option) => (
										<MenuItem
											{...props}
											sx={{ minHeight: '40px !important', whiteSpace: 'normal', lineHeight: 1.3 }}
											component="li"
											divider={option.id === NEW_SKU_VALUE}
										>
											{option.label}
										</MenuItem>
									)}
									size="medium"
								/>
							</Grid>
							{userLocations.length > 1 && (
								<Grid item xs={12} sm={6}>
									<TextField
										id="type"
										label={t('inventory.chooseLocationLabel', 'Store')}
										name="type"
										select
										className={classes.textField}
										value={selectedLocationId}
										onChange={handleLocationChange}
										margin="none"
										type="text"
										variant="outlined"
										fullWidth
									>
										<MenuItem value="" disabled>
											{t('inventory.chooseLocation', 'Choose store')}
										</MenuItem>
										{userLocations.map((location) => (
											<MenuItem value={location.id} key={location.id}>
												{location.name}
											</MenuItem>
										))}
									</TextField>
								</Grid>
							)}
						</Grid>
						<Box mt={4} />
						<Box>
							<Box mb={2}>
								<Typography variant="body1">
									{t('inventory.addArticles.specifications.title', 'Specifications')}
								</Typography>
							</Box>
							<Box className={classes.grayBackground} py={4} px={2}>
								{!!inventoryFields.length || !!visibleFixedValueOptions.length ? (
									<>
										<SpecificationsList
											fields={inventoryFields}
											fieldValues={tagFieldsToAdd}
											setFieldValues={setTagFieldsToAdd}
										/>
										<Box mt={2}>
											<FixedFieldValuesList
												fixedFieldValueOptions={visibleFixedValueOptions}
												fixedFieldValues={fixedFieldValues ?? {}}
												setFixedFieldValues={setFixedFieldValues}
											/>
										</Box>
										<Box mt={4}>
											<Button
												variant="outlined"
												color="primary"
												onClick={() => setNewSpecificationDialogOpen(true)}
											>
												+{' '}
												{t('inventory.addArticles.specifications.addNew', 'Add new specification')}
											</Button>
										</Box>
									</>
								) : (
									<Box
										display="flex"
										justifyContent="center"
										alignItems="center"
										flexDirection="column"
									>
										<>
											<Typography variant="body1">
												{t(
													'inventory.addArticles.specifications.desc',
													'Use Specifications to add meta information about your article.',
												)}
											</Typography>
											<Typography variant="body1">
												{t(
													'inventory.addArticles.specifications.examples',
													'E.g. Make, Model, Color.',
												)}
											</Typography>
											<Box mt={4}>
												<Button
													variant="outlined"
													color="primary"
													onClick={() => setNewSpecificationDialogOpen(true)}
												>
													+{' '}
													{t(
														'inventory.addArticles.specifications.addNew',
														'Add new specification',
													)}
												</Button>
											</Box>
										</>
									</Box>
								)}
							</Box>
						</Box>
						<Box mt={4} />
						{selectedSkuType === 'single' && (
							<Box>
								<Typography variant="body1">
									{t('inventory.addArticles.identifiers', 'Identifiers')}
								</Typography>
								<FormControl className={classes.paymentSelection}>
									<RadioGroup value={autoIds ? 'auto' : 'custom'} onChange={handleAutoIdsChange}>
										<FormControlLabel
											value="auto"
											control={<Radio color="primary" />}
											label={t('inventory.addArticles.autoGenerate', 'Auto-generate identifiers')}
										/>
										<FormControlLabel
											value="custom"
											control={<Radio color="primary" />}
											label={t('inventory.addArticles.addCustom', 'Add custom identifiers')}
										/>
									</RadioGroup>
								</FormControl>
							</Box>
						)}
						{autoIds || selectedSkuType !== 'single'
							? renderAmountSelectorAndAllocation()
							: renderCustomIdSelector()}
						<Box mt={4} mb={4}>
							<Divider />
						</Box>
						<Grid container spacing={2} direction="column" alignItems="center">
							<Grid item>
								<Button
									variant="contained"
									color="primary"
									onClick={() => addStockItem()}
									disabled={isAddingNewItems || !selectedSkuId || amount <= 0}
								>
									{' '}
									{isAddingNewItems ? (
										<Spinner padding={8} />
									) : (
										<Typography variant="button" color="inherit">
											{amount === 0 || amount > 1
												? t('inventory.addArticles.addTheProducts', {
														amount: amount,
														defaultValue: 'Add {{amount}} articles',
												  })
												: t('inventory.addArticles.addTheProduct', {
														amount: amount,
														defaultValue: 'Add {{amount}} article',
												  })}
										</Typography>
									)}
								</Button>
							</Grid>
							<Grid item>
								<Button
									variant="text"
									color="primary"
									onClick={backToInventory}
									disabled={isAddingNewItems}
								>
									<Typography variant="button" color={'secondary'}>
										{t('common:actions.goBack')}
									</Typography>
								</Button>
							</Grid>
						</Grid>
					</ContainerBox>
				)}
			</Container>
			<AddSku
				open={addSkuOpen}
				onCancel={() => setAddSkuOpen(false)}
				onCreate={handleSkuCreated}
				skuItems={skuItems}
				shopId={shopId}
			/>
			{isNewSpecificationDialogOpen && (
				<NewSpecificationDialog
					isOpen={isNewSpecificationDialogOpen}
					onClose={() => setNewSpecificationDialogOpen(false)}
					existingFieldLabels={inventoryFields.map((f) => f.label)}
					handleAdd={handleAddNewInventoryField}
				/>
			)}
		</>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		textField: {
			margin: theme.spacing(1),
			minWidth: 200,
			[theme.breakpoints.down('md')]: {
				marginLeft: 0,
			},
		},
		paymentSelection: {
			padding: '16px 0',
		},
		addUnit: {
			fontSize: '16px',
		},
		rootBig: {
			width: '100%',
			margin: '24px 0 0 0',
		},
		marginTop24: {
			marginTop: theme.spacing(3),
		},
		marginTop16: {
			marginTop: theme.spacing(2),
		},
		marginBottom16: {
			marginBottom: theme.spacing(2),
		},
		chipContainer: {
			overflow: 'auto',
			height: '200px',
			margin: theme.spacing(1),
			alignContent: 'flex-start',
			border: `1px solid ${theme.palette.primary.main}`,
			borderRadius: 4,
		},
		chipLabel: {
			padding: 8,
			paddingBottom: 0,
			backgroundColor: theme.palette.background.paper,
			zIndex: 2,
			top: 2,
			transform: 'translate(14px,-6px)scale(0.75)',
		},
		grayBackground: {
			backgroundColor: theme.palette.background.default,
		},
		snackBar: {
			backgroundColor: theme.palette.success.main,
			color: theme.palette.success.contrastText,
			textAlign: 'center',
		},
		snackBarText: {
			padding: theme.spacing(1),
		},
		ProductToAdd: {
			marginLeft: 0,
		},
		justifyCenter: {
			justifyContent: 'center',
			padding: theme.spacing(4),
		},
		'@global': {
			'#chipInput': {
				paddingBottom: 4,
				alignSelf: 'center',
			},
			'#chipInput + fieldset': {
				border: 'none',
			},
		},
	}),
);

export default AddInventory;
